// Shared helpers for org-graph presentational primitives.
//
// Co-locating these helpers keeps PersonNode/PersonHoverCard small and avoids
// duplicating identical 5-line snippets across the four primitive components
// (SonarCloud's CPD gate is unforgiving in this repo — see CLAUDE.md).

import type { IOrgGraphNode } from '@skystone/shared';

/** Maximum number of characters to show in the visible label under a node. */
export const MAX_LABEL_CHARS = 16;

// `Intl.Segmenter` is part of ES2022.Intl but the frontend's tsconfig.json
// targets ES2020 lib — declare a minimal local typing rather than widening
// the project-wide lib option for one helper.
interface SegmenterSegment {
  segment: string;
}
interface SegmenterLike {
  segment(input: string): Iterable<SegmenterSegment>;
}
type SegmenterConstructor = new (
  locales?: string | string[],
  options?: { granularity?: 'grapheme' | 'word' | 'sentence' },
) => SegmenterLike;

/**
 * Split a string into user-perceived grapheme clusters.
 *
 * Prefers `Intl.Segmenter` (a built-in cluster segmenter — modern Chromium,
 * Firefox, Safari, Node 16+) so ZWJ-joined emoji, combining accents, and
 * country-flag sequences count as one grapheme. Falls back to `Array.from`
 * (code-point split) when `Intl.Segmenter` is unavailable; behaviour
 * degrades to "may split a multi-code-point cluster" rather than throwing.
 */
function graphemes(value: string): string[] {
  const SegmenterCtor = (Intl as unknown as { Segmenter?: SegmenterConstructor }).Segmenter;
  if (typeof SegmenterCtor === 'function') {
    const segmenter = new SegmenterCtor(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(value), (s) => s.segment);
  }
  return Array.from(value);
}

/**
 * Extract initials from a display name. Grapheme-cluster-safe via
 * `Intl.Segmenter`, so ZWJ-joined emoji, combining accents (e.g. "Á"),
 * and flag sequences count as one user-perceived character.
 *
 * Rules:
 *  - Split on whitespace.
 *  - ≥2 parts → first grapheme of the first part + first grapheme of the last
 *    part (e.g. "Anna Persson" → "AP", "Christina Bergstrand-Lindqvist" → "CB").
 *  - Otherwise → first two graphemes of the trimmed name (e.g. "Liam" → "LI").
 *  - Result is upper-cased.
 */
export function initialsOf(node: Pick<IOrgGraphNode, 'displayName'>): string {
  const trimmed = node.displayName.trim();
  if (trimmed.length === 0) return '';
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    const first = graphemes(parts[0])[0] ?? '';
    const last = graphemes(parts.at(-1)!)[0] ?? '';
    return (first + last).toUpperCase();
  }
  return graphemes(trimmed).slice(0, 2).join('').toUpperCase();
}

/**
 * Truncate a label to {@link MAX_LABEL_CHARS} grapheme clusters, appending an
 * ellipsis when truncation actually occurs. Cluster-safe via `Intl.Segmenter`
 * so combining sequences and ZWJ emoji are never split mid-character.
 */
export function truncateLabel(name: string, max = MAX_LABEL_CHARS): string {
  const clusters = graphemes(name);
  if (clusters.length <= max) return name;
  return clusters.slice(0, max).join('') + '…';
}
